import { useState } from 'react';
import type { FormEvent } from 'react';
import { NOTE_MAX } from '@iac/contracts';
import type { BrainNoteDto } from '@iac/contracts';
import { Dialog } from '../../components/Dialog';
import { useDict } from '../../i18n/dictionary';
import { useCreateNote, useUpdateNote } from './api';
import { brainDict } from './i18n';

/**
 * Adding a note when `note` is absent, editing it otherwise. Shared by the
 * Notes page and the ask screen's source cards, so both correct a fact the
 * same way.
 */
export function NoteDialog({
  open,
  note,
  onClose,
  onSaved,
}: {
  open: boolean;
  note?: BrainNoteDto;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const t = useDict(brainDict);
  const create = useCreateNote();
  const update = useUpdateNote();

  // Closed on success only: a note is embedded before it is stored, so an
  // upstream failure must leave what was typed where it is.
  const saved = {
    onSuccess: () => {
      onClose();
      onSaved?.();
    },
  };

  return (
    <Dialog
      open={open}
      title={note ? t.editNote : t.rememberSomething}
      onClose={onClose}
    >
      <NoteForm
        key={note?.id ?? 'new'}
        note={note}
        submitLabel={note ? t.save : t.remember}
        pending={note ? update.isPending : create.isPending}
        onSubmit={(content) =>
          note
            ? update.mutate({ id: note.id, content }, saved)
            : create.mutate({ content }, saved)
        }
        onCancel={onClose}
      />
    </Dialog>
  );
}

/** `note` seeds the text once. */
function NoteForm({
  note,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  note?: BrainNoteDto;
  submitLabel: string;
  pending: boolean;
  onSubmit: (content: string) => void;
  onCancel: () => void;
}) {
  const t = useDict(brainDict);
  const [content, setContent] = useState(note?.content ?? '');

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit(content);
  }

  return (
    <form onSubmit={submit} className="form-stack">
      <div>
        <label htmlFor="note-content" className="field-label">
          {t.note}
        </label>
        <textarea
          id="note-content"
          className="field-input note-input"
          rows={5}
          required
          maxLength={NOTE_MAX}
          autoFocus
          placeholder={note ? undefined : t.notePlaceholder}
          value={content}
          onChange={(event) => setContent(event.target.value)}
        />
      </div>
      <div className="form-actions">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          {t.cancel}
        </button>
        <button type="submit" className="btn-primary" disabled={pending}>
          {pending ? t.saving : submitLabel}
        </button>
      </div>
    </form>
  );
}
